import { useRef } from 'react';

/**
 * Clears a react-virtualized CellMeasurerCache during render whenever one of
 * the inputs that can change row heights differs from the previous render.
 *
 * It runs in the render phase on purpose. CellMeasurer re-measures in
 * componentDidMount/componentDidUpdate only when its row index is missing from
 * the cache, and children commit before a parent's effects run, so a clear in
 * an effect would leave the rows unmeasured until something else re-rendered
 * the list. Clearing on every render instead (the previous behaviour) forced
 * a layout per mounted row on every unrelated re-render of the column.
 *
 * The first render always clears: the caches are global per droppable id and
 * survive unmount, so a remounted column must not inherit stale heights.
 *
 * @param {{ clearAll: Function }} cache a CellMeasurerCache
 * @param {Array} inputs values compared with Object.is against the previous render
 */
const useClearCacheOnLayoutChange = (cache, inputs) => {
  const previousRef = useRef(null);
  const previous = previousRef.current;
  const changed = previous === null
    || previous.length !== inputs.length
    || inputs.some((value, index) => !Object.is(value, previous[index]));
  if (changed) {
    cache.clearAll();
    previousRef.current = inputs;
  }
};

export default useClearCacheOnLayoutChange;
